import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, FileText, User, Building2, Calendar,
  ChevronRight, UserCheck, FileEdit, AlertTriangle,
  UserMinus, CheckCircle2, Trash2
} from 'lucide-react';
import { Card, Breadcrumb, Button, Loading, LogoLoadingMark } from '../../components/ui';
import { laporanService } from '../../services/laporan';
import { useAuthStore } from '../../store/authStore';
import { useCapabilities } from '../../hooks/useCapabilities';
import toast from 'react-hot-toast';

const statusLabels: Record<string, { label: string; color: string }> = {
  draf: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  diajukan: { label: 'Diajukan', color: 'bg-blue-100 text-blue-700' },
  validasi_l1: { label: 'Valid L1', color: 'bg-cyan-100 text-cyan-700' },
  ditolak_l1: { label: 'Ditolak L1', color: 'bg-red-100 text-red-700' },
  validasi_l2: { label: 'Valid L2', color: 'bg-teal-100 text-teal-700' },
  ditolak_l2: { label: 'Ditolak L2', color: 'bg-red-100 text-red-700' },
  antrean_rapim: { label: 'Antrean RAPIM', color: 'bg-purple-100 text-purple-700' },
  diputuskan: { label: 'Diputuskan', color: 'bg-indigo-100 text-indigo-700' },
  tugas_ppepp_aktif: { label: 'PPEPP Aktif', color: 'bg-teal-100 text-teal-700' },
  berlangsung: { label: 'Berlangsung', color: 'bg-yellow-100 text-yellow-700' },
  menunggu_konfirmasi: { label: 'Menunggu Konfirmasi', color: 'bg-purple-100 text-purple-700' },
  selesai: { label: 'Selesai', color: 'bg-green-100 text-green-700' },
  diarsipkan: { label: 'Arsip', color: 'bg-gray-100 text-gray-500' }
};

const risikoLabels: Record<string, { label: string; color: string }> = {
  rendah: { label: 'Rendah', color: 'bg-green-100 text-green-700' },
  sedang: { label: 'Sedang', color: 'bg-yellow-100 text-yellow-700' },
  tinggi: { label: 'Tinggi', color: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Kritis', color: 'bg-red-100 text-red-700' }
};

type ScopeTab = 'semua' | 'pelapor' | 'pic';

const LaporanList: React.FC = () => {
  const { user } = useAuthStore();
  const { canCreateLaporan } = useCapabilities();
  const userId = user?.id;

  const canCreate = canCreateLaporan;

  const [searchParams, setSearchParams] = useSearchParams();
  const [laporan, setLaporan] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [risikoFilter, setRisikoFilter] = useState('');
  const [scopeTab, setScopeTab] = useState<ScopeTab>((searchParams.get('scope') as ScopeTab) || 'semua');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);

  // State untuk delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingLaporan, setDeletingLaporan] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchLaporan = async (page = currentPage, scope = scopeTab) => {
    try {
      setLoading(true);
      const params: Record<string, any> = { page, per_page: 10 };
      if (search) params.search = search;

      if (statusFilter === 'overdue') {
        params.overdue = true;
      } else if (statusFilter) {
        params.status = statusFilter;
      }

      if (risikoFilter) params.tingkat_risiko = risikoFilter;
      if (scope !== 'semua') params.scope = scope;

      const response = await laporanService.getAll(params);
      setLaporan(response.data || []);
      if (response.meta) {
        setPagination(response.meta);
      }
    } catch (err) {
      console.error('Failed to load laporan:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlScope = searchParams.get('scope') as ScopeTab;
    const urlStatus = searchParams.get('status');
    if (urlScope && ['semua', 'pelapor', 'pic'].includes(urlScope)) {
      setScopeTab(urlScope);
    }
    if (urlStatus) {
      setStatusFilter(urlStatus);
    }
  }, []);

  useEffect(() => {
    fetchLaporan(currentPage, scopeTab);
  }, [currentPage, statusFilter, risikoFilter, scopeTab]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLaporan(1, scopeTab);
  };

  const handleScopeChange = (scope: ScopeTab) => {
    setScopeTab(scope);
    setCurrentPage(1);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('scope', scope);
    setSearchParams(newParams);
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams);
  };

  // Handle delete draft
  const handleDelete = async () => {
    if (!deletingLaporan) return;

    setDeleting(true);
    try {
      await laporanService.delete(deletingLaporan.id);
      toast.success('Draft berhasil dihapus');
      setDeleteModalOpen(false);
      setDeletingLaporan(null);
      fetchLaporan(currentPage, scopeTab);
    } catch (err: any) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.message || 'Gagal menghapus draft');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (lap: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeletingLaporan(lap);
    setDeleteModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const config = statusLabels[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>{config.label}</span>;
  };

  const getRisikoBadge = (risiko: string) => {
    const config = risikoLabels[risiko] || { label: risiko, color: 'bg-gray-100 text-gray-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>{config.label}</span>;
  };

  const getRoleBadge = (lap: any) => {
    const badges: React.ReactNode[] = [];
    if (!userId) return badges;

    const isPelapor = lap.is_pelapor === true || lap.user_id === userId;
    const isPicAktif = lap.is_pic_aktif === true;
    const isBekasPic = lap.is_bekas_pic === true;
    const picHistoryStatus = lap.pic_history_status;

    if (isPelapor) {
      badges.push(
        <span key="pelapor" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200" title="Anda yang membuat laporan ini">
          <FileEdit className="w-3 h-3" />
          Pelapor
        </span>
      );
    }

    if (isPicAktif) {
      badges.push(
        <span key="pic-aktif" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200" title="Anda adalah PIC aktif">
          <UserCheck className="w-3 h-3" />
          PIC Aktif
        </span>
      );
    } else if (isBekasPic) {
      if (picHistoryStatus === 'dialihkan') {
        badges.push(
          <span key="dialihkan" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-600 border border-orange-200" title="Anda pernah jadi PIC, sudah dialihkan">
            <UserMinus className="w-3 h-3" />
            Dialihkan
          </span>
        );
      } else if (picHistoryStatus === 'selesai') {
        badges.push(
          <span key="selesai" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-600 border border-green-200" title="Laporan telah selesai">
            <CheckCircle2 className="w-3 h-3" />
            Selesai
          </span>
        );
      } else {
        badges.push(
          <span key="bekas-pic" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200" title="Anda pernah jadi PIC">
            <UserCheck className="w-3 h-3" />
            Bekas PIC
          </span>
        );
      }
    }

    return badges;
  };

  const isLaporanOverdue = (lap: any): boolean => {
    const status = (lap.status || '').toLowerCase();
    if (['selesai', 'diarsipkan'].includes(status)) return false;

    const target = lap.target_efektif || lap.target_selesai || lap.targetSelesai;
    if (target && new Date(target) < new Date()) return true;

    const ppepp = lap.active_ppepp_rekomendasi ?? lap.activePpeppRekomendasi;
    if (ppepp?.target_date && !['selesai', 'batal', 'dibatalkan'].includes((ppepp.status || '').toLowerCase())) {
      return new Date(ppepp.target_date) < new Date();
    }

    return false;
  };

  // Cek apakah user bisa hapus draft ini
  const canDeleteDraft = (lap: any): boolean => {
    const isDraft = lap.status === 'draf';
    const isOwner = lap.user_id === userId || lap.user?.id === userId;
    return isDraft && isOwner;
  };

  // Format kode untuk tampilan (hide DRAFT- prefix)
  const formatKode = (kode: string): string => {
    if (kode && kode.startsWith('DRAFT-')) {
      return 'Draft';
    }
    return kode;
  };

  const scopeTabs = [
    { key: 'semua' as ScopeTab, label: 'Semua Laporan', icon: <FileText className="w-4 h-4" /> },
    { key: 'pelapor' as ScopeTab, label: 'Laporan Saya', icon: <FileEdit className="w-4 h-4" /> },
    { key: 'pic' as ScopeTab, label: 'Saya Sebagai PIC', icon: <UserCheck className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan Masalah' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Laporan Masalah</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Kelola semua laporan evaluasi masalah</p>
        </div>
        {canCreate && (
          <Link to="/laporan/buat">
            <Button variant="primary" icon={<Plus className="w-4 h-4" />}>Buat Laporan</Button>
          </Link>
        )}
      </div>

      {/* Scope Tabs */}
      <div className="flex items-center gap-1 border-b border-neutral-border overflow-x-auto">
        {scopeTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleScopeChange(tab.key)}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${scopeTab === tab.key
              ? 'border-teal-main text-teal-main'
              : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary hover:border-neutral-border'
              }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Info Box untuk Tab PIC */}
      {scopeTab === 'pic' && (
        <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl">
          <p className="text-xs text-teal-700">
            <strong>Info:</strong> Tab ini menampilkan laporan dimana Anda ditunjuk sebagai PIC.
            <span className="font-semibold text-emerald-600"> PIC Aktif</span> = sedang bertanggung jawab,
            <span className="font-semibold text-orange-600"> Dialihkan</span> = sudah diganti PIC lain,
            <span className="font-semibold text-green-600"> Selesai</span> = laporan telah selesai.
          </p>
        </div>
      )}

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
            <input
              type="text"
              placeholder="Cari laporan..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-light"
            />
          </div>
          <select value={statusFilter} onChange={e => handleStatusChange(e.target.value)} className="text-sm border border-neutral-border rounded-lg px-3 py-2.5 bg-white">
            <option value="">Semua Status</option>
            <option value="draf">Draft</option>
            <option value="diajukan">Diajukan</option>
            <option value="validasi_l1">Valid L1</option>
            <option value="validasi_l2">Valid L2</option>
            <option value="antrean_rapim">Antrean RAPIM</option>
            <option value="diputuskan">Diputuskan RAPIM</option>
            <option value="berlangsung">Berlangsung</option>
            <option value="menunggu_konfirmasi">Menunggu Konfirmasi</option>
            <option value="selesai">Selesai</option>
            <option value="diselesaikan_lokal">Ditangani Langsung</option>
            <option value="diarsipkan">Diarsipkan</option>
            <option value="ditolak_l1">Ditolak L1</option>
            <option value="ditolak_l2">Ditolak L2</option>
            <option value="overdue">⚠️ Terlambat</option>
          </select>
          <select value={risikoFilter} onChange={e => { setRisikoFilter(e.target.value); setCurrentPage(1); }} className="text-sm border border-neutral-border rounded-lg px-3 py-2.5 bg-white">
            <option value="">Semua Risiko</option>
            <option value="rendah">Rendah</option>
            <option value="sedang">Sedang</option>
            <option value="tinggi">Tinggi</option>
            <option value="critical">Kritis</option>
          </select>
        </div>
      </Card>

      {/* List View */}
      <div className="space-y-3">
        {loading ? (
          <Card className="text-center py-12">
            <Loading variant="inline" message="Memuat data..." className="py-2" />
          </Card>
        ) : laporan.length === 0 ? (
          <Card className="text-center py-12">
            <FileText className="w-12 h-12 text-neutral-text-secondary mx-auto mb-3 opacity-40" />
            <p className="text-base font-semibold text-neutral-text-primary mb-1">
              {statusFilter === 'overdue' ? 'Tidak ada laporan terlambat' :
                scopeTab === 'pelapor' ? 'Belum ada laporan yang Anda buat' :
                  scopeTab === 'pic' ? 'Anda belum pernah ditunjuk sebagai PIC' :
                    'Belum ada laporan'}
            </p>
            <p className="text-sm text-neutral-text-secondary">
              {scopeTab === 'pelapor' && canCreate ? 'Mulai buat laporan masalah pertama Anda.' :
                scopeTab === 'pic' ? 'Laporan akan muncul ketika Anda ditunjuk sebagai PIC oleh RAPIM.' :
                  'Tidak ada data untuk filter ini.'}
            </p>
          </Card>
        ) : (
          laporan.map((lap: any) => {
            const roleBadges = getRoleBadge(lap);
            const overdue = isLaporanOverdue(lap);

            return (
              <Link key={lap.id} to={`/laporan/${lap.id}`}>
                <Card hover className={`group ${overdue ? 'border-l-4 border-l-danger' : ''}`}>
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-20 text-center">
                      <div className={`text-xs font-mono font-bold ${lap.status === 'draf' ? 'text-gray-400' : 'text-teal-main'}`}>
                        {formatKode(lap.kode)}
                      </div>
                      <div className="mt-1 w-3 h-3 rounded-full mx-auto" style={{ backgroundColor: lap.kategori_masalah?.color || lap.kategoriMasalah?.color || '#6B7280' }} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-bold text-neutral-text-primary truncate group-hover:text-teal-main transition-colors">
                              {lap.judul}
                            </h3>
                            {roleBadges}
                            {overdue && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">
                                <AlertTriangle className="w-3 h-3" />
                                Terlambat
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-neutral-text-secondary mt-1 line-clamp-2">
                            {lap.deskripsi || 'Tidak ada deskripsi'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {getStatusBadge(lap.status)}
                          {getRisikoBadge(lap.tingkat_risiko || lap.tingkatRisiko)}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-neutral-text-secondary">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {lap.user?.name || '-'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {lap.unit?.name || '-'}
                        </span>
                        <span className={`flex items-center gap-1 ${overdue ? 'text-danger font-semibold' : ''}`}>
                          <Calendar className="w-3.5 h-3.5" />
                          {(lap.target_efektif || lap.target_selesai || lap.targetSelesai)
                            ? new Date(lap.target_efektif || lap.target_selesai || lap.targetSelesai).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
                            : '-'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          {lap.completeness_percentage ?? lap.completenessPercentage ?? 0}%
                        </span>

                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {/* Tombol Hapus Draft */}
                      {canDeleteDraft(lap) && (
                        <button
                          onClick={(e) => openDeleteModal(lap, e)}
                          className="p-2 rounded-lg text-neutral-text-secondary hover:text-danger hover:bg-danger/10 transition-colors"
                          title="Hapus Draft"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <ChevronRight className="w-5 h-5 text-neutral-text-secondary group-hover:text-teal-main" />
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.last_page > 1 && (
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-text-secondary">
              Menampilkan {laporan.length} dari {pagination.total} data
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 text-xs font-medium rounded-lg text-neutral-text-secondary hover:bg-neutral-bg disabled:opacity-50">
                Sebelumnya
              </button>
              {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                let page = pagination.last_page <= 5 ? i + 1 : currentPage <= 3 ? i + 1 : currentPage >= pagination.last_page - 2 ? pagination.last_page - 4 + i : currentPage - 2 + i;
                return (
                  <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${page === currentPage ? 'bg-teal-main text-white' : 'text-neutral-text-secondary hover:bg-neutral-bg'}`}>
                    {page}
                  </button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))} disabled={currentPage === pagination.last_page} className="px-3 py-1.5 text-xs font-medium rounded-lg text-neutral-text-secondary hover:bg-neutral-bg disabled:opacity-50">
                Selanjutnya
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-text-primary">Hapus Draft?</h3>
                <p className="text-sm text-neutral-text-secondary">Tindakan ini tidak dapat dibatalkan</p>
              </div>
            </div>

            {deletingLaporan && (
              <div className="p-3 bg-neutral-bg rounded-xl mb-4 border border-neutral-border">
                <p className="text-xs text-gray-400 font-mono tracking-wider">{formatKode(deletingLaporan.kode)}</p>
                <p className="text-sm font-medium text-neutral-text-primary mt-1 line-clamp-2 italic">
                  {deletingLaporan.judul || '(Tanpa Judul)'}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeletingLaporan(null);
                }}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-neutral-text-secondary hover:text-neutral-text-primary hover:bg-neutral-bg rounded-lg transition-colors"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all shadow-md active:scale-95 disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <LogoLoadingMark size="xs" />
                    Menghapus...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Hapus Draft
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaporanList;
